// src>pages>Bidstrategies
import React, { useState, useEffect, useRef, useMemo } from "react";
import { FaFilter, FaColumns, FaExpand, FaCompress } from "react-icons/fa";
import { IoIosArrowDown } from "react-icons/io";
import { FaChevronRight, FaChevronLeft } from "react-icons/fa6";
import { MdOutlineSegment, MdOutlineFileDownload } from "react-icons/md";
import axios from "axios";
import { format } from "date-fns";
import { useReactToPrint } from "react-to-print";
import { IoCodeSharp } from "react-icons/io5";
import ModifyColumns from "./Tools/ModifyColumns";
import { IoMdClose } from "react-icons/io";
import {
  apiRequestBodyBuilder,
  FilterComponent,
  getFilteredData,
  getPriorityColumnsFromStorage,
  getSortableValueGLOBAL,
  renderTableCell,
  renderTableTotal,
} from "./config/tableHelper";
import { DateRangeSelector } from "./../styles/CustomDateRangePicker";
import { RenderTable } from "./config/tableWrapper";
import { generatePDF, generateCSV } from "./Components/DownloadFuncs";

// Skeleton styled identically to PaidAdsPage TableSkeleton
function TableSkeleton() {
  return (
    <div className="w-full rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      {/* Header Bar */}
      <div className="flex items-center justify-between pb-4 border-b border-gray-100 animate-pulse">
        <div className="h-4 w-32 bg-gray-200 rounded" />
        <div className="flex items-center gap-8 sm:gap-12">
          <div className="h-4 w-16 bg-gray-200 rounded hidden sm:block" />
          <div className="h-4 w-16 bg-gray-200 rounded" />
          <div className="h-4 w-20 bg-gray-200 rounded" />
          <div className="h-4 w-24 bg-gray-200 rounded hidden md:block" />
        </div>
      </div>

      {/* Rows */}
      <div className="divide-y divide-gray-100 animate-pulse">
        {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
          <div key={item} className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <div
                className="h-4 bg-gray-200 rounded"
                style={{ width: `${140 + (item % 3) * 35}px` }}
              />
            </div>
            <div className="flex items-center gap-8 sm:gap-12">
              <div className="h-4 w-14 bg-gray-100 rounded hidden sm:block" />
              <div className="h-4 w-16 bg-gray-100 rounded" />
              <div className="h-4 w-16 bg-gray-100 rounded" />
              <div className="h-4 w-20 bg-gray-100 rounded hidden md:block" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// BidStrategyColumns

export const BID_STRATEGY_COLUMNS = [
  {
    id: "1",
    title: "Bid Strategy Name",
    key: "name",
    type: "string",
    visible: true,
    category: "Recommended",
    isLocked: true,
    sticky: true,
  },
  {
    id: "2",
    title: "Status",
    key: "status",
    type: "boolean",
    visible: true,
    category: "Recommended",
    isLocked: false,
  },
  {
    id: "3",
    title: "Type",
    key: "type",
    type: "boolean",
    visible: true,
    category: "Recommended",
    isLocked: false,
  },
  {
    id: "4",
    title: "Clicks",
    key: "clicks",
    type: "number",
    visible: true,
    category: "Performance",
    isLocked: false,
  },
  {
    id: "5",
    title: "Impr.",
    key: "impressions",
    type: "number",
    visible: true,
    category: "Performance",
    isLocked: false,
  },
  {
    id: "6",
    title: "Cost",
    key: "cost",
    type: "currency",
    visible: true,
    category: "Performance",
    isLocked: false,
  },
  {
    id: "7",
    title: "CTR",
    key: "ctr",
    type: "float",
    visible: true,
    category: "Performance",
    isLocked: false,

    calculation: {
      numerator: "clicks",
      denominator: "impressions",
      multiplier: 100,
    },
  },
  {
    id: "8",
    title: "Avg. CPC",
    key: "average_cpc",
    type: "currency",
    visible: true,
    category: "Performance",
    isLocked: false,
    calculation: { numerator: "cost", denominator: "clicks" },
  },
  {
    id: "9",
    title: "Conversions",
    key: "conversions",
    type: "float-no-perc",
    visible: true,
    category: "Conversions",
    isLocked: false,
  },
  {
    id: "10",
    title: "Conv. Rate",
    key: "conversions_rate",
    type: "float",
    visible: false,
    category: "Conversions",
    isLocked: false,

    calculation: {
      numerator: "conversions",
      denominator: "clicks",
      multiplier: 100,
    },
  },
  {
    id: "11",
    title: "Cost / Conv.",
    key: "cost_per_conversion",
    type: "currency",
    visible: false,
    category: "Conversions",
    isLocked: false,
    calculation: { numerator: "cost", denominator: "conversions" },
  },
  {
    id: "12",
    title: "Campaign Count",
    key: "campaign_count",
    type: "number",
    visible: false,
    category: "Attributes",
    isLocked: false,
  },
  {
    id: "13",
    title: "Bid Strategy ID",
    key: "bid_strategy_id",
    type: "string",
    visible: false,
    category: "Attributes",
    isLocked: false,
  },
];

export const BID_STRATEGY_DATE_SEGMENTS_COLUMNS = [
  {
    id: "1",
    title: "Bid Strategy",
    key: "bid_strategy",
    type: "string",
    visible: true,
    category: "Recommended",
    isLocked: true,
    sticky: true,
  },
  {
    id: "15",
    title: "Date",
    key: "date",
    type: "string",
    visible: true,
    category: "Segments",
    isLocked: true,
  },
  {
    id: "3",
    title: "Strategy Type",
    key: "type",
    type: "string",
    visible: true,
    category: "Recommended",
    isLocked: false,
  },
  {
    id: "4",
    title: "Status",
    key: "status",
    type: "boolean",
    visible: true,
    category: "Recommended",
    isLocked: false,
  },
  {
    id: "5",
    title: "Campaigns",
    key: "campaign_count",
    type: "number",
    visible: true,
    category: "Recommended",
    format: "comma",
    isLocked: false,
  },
  {
    id: "6",
    title: "Impr.",
    key: "impressions",
    type: "number",
    visible: true,
    category: "Performance",
    format: "comma",
    isLocked: false,
  },
  {
    id: "7",
    title: "Clicks",
    key: "clicks",
    type: "number",
    visible: true,
    category: "Performance",
    format: "comma",
    isLocked: false,
  },
  {
    id: "8",
    title: "CTR",
    key: "ctr",
    type: "float",
    visible: true,
    category: "Performance",
    format: "percentage",
    isLocked: false,
    calculation: {
      numerator: "clicks",
      denominator: "impressions",
      multiplier: 100,
    },
  },
  {
    id: "9",
    title: "Avg. CPC",
    key: "avg_cpc",
    type: "currency",
    visible: true,
    category: "Performance",
    format: "rupees",
    isLocked: false,
    calculation: { numerator: "cost", denominator: "clicks" },
  },
  {
    id: "10",
    title: "Cost",
    key: "cost",
    type: "currency",
    visible: true,
    category: "Performance",
    format: "rupees",
    isLocked: false,
  },
  {
    id: "11",
    title: "Conversions",
    key: "conversions",
    type: "float-no-perc",
    visible: true,
    category: "Conversion",
    format: "comma",
    isLocked: false,
  },
  {
    id: "12",
    title: "Conv. Rate",
    key: "conversion_rate",
    type: "float",
    visible: true,
    category: "Conversion",
    format: "percentage",
    isLocked: false,
    calculation: {
      numerator: "conversions",
      denominator: "clicks",
      multiplier: 100,
    },
  },
  {
    id: "13",
    title: "Cost / Conv.",
    key: "cost_per_conversion",
    type: "currency",
    visible: true,
    category: "Conversion",
    format: "rupees",
    isLocked: false,
    calculation: { numerator: "cost", denominator: "conversions" },
  },
  {
    id: "14",
    title: "Day of Week",
    key: "day_of_week",
    type: "string",
    visible: false,
    category: "Segments",
    isLocked: false,
  },
];

const BidStrategies = () => {
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterArray, setFilterArray] = useState([]);
  const [showColumnsMenu, setShowColumnsMenu] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [columns, setColumns] = useState([]);
  const [tableVisible, setTableVisible] = useState(true);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [random, setRandom] = useState(Math.random());
  const [isDownloading, setIsDownloading] = useState(false);

  let d = new Date();
  let year = d.getFullYear();
  let month = d.getMonth();
  const [state, setState] = useState([
    {
      startDate: new Date(year, month, 1),
      endDate: new Date(),
      key: "selection",
    },
  ]);

  const [segmentType, setSegmentType] = useState("default");
  const [dateSegments, setDateSegments] = useState("date");
  const [loading, setIsLoading] = useState(true);

  const exportRef = useRef(null);
  const downloadRef = useRef(null);

  const filteredData = useMemo(() => {
    return getFilteredData(Array.isArray(data) ? data : [], filterArray);
  }, [data, filterArray]);

  const handleDownload = async (type) => {
    setShowDownloadMenu(false);
    setIsDownloading(true);
    try {
      const filename = `Bid_Strategy_Report_${format(new Date(), "yyyy-MM-dd")}.${type}`;
      const dateRangeStr =
        state[0].startDate === state[0].endDate
          ? "All time"
          : `${format(state[0].startDate, "MMM dd, yyyy")} - ${format(state[0].endDate, "MMM dd, yyyy")}`;

      // Delay slightly so UI spinner renders
      await new Promise((resolve) => setTimeout(resolve, 100));

      if (type === "pdf") {
        await generatePDF(
          filteredData,
          columns,
          dateRangeStr,
          filename,
          "Bid Strategy report",
        );
      } else if (type === "csv") {
        await generateCSV(
          filteredData,
          columns,
          dateRangeStr,
          filename,
          "Bid Strategy report",
        );
      }
    } catch (error) {
      console.error(`Failed to generate ${type.toUpperCase()}:`, error);
    } finally {
      setIsDownloading(false);
    }
  };

  const updateSegmentColumns = (cols, type) => {
    return cols.map((item) => {
      if (
        ["date", "week", "month", "quarter", "year", "segment"].includes(
          item.key,
        )
      ) {
        return {
          ...item,
          title:
            type === "date"
              ? "Date"
              : type.charAt(0).toUpperCase() + type.slice(1),
          visible: true,
        };
      }
      return item;
    });
  };

  const changeGroupbyView = (segment, dateSegment = "none") => {
    if (dateSegment !== "none") {
      setDateSegments(dateSegment);
    }
    setSegmentType(segment);
    setRandom(Math.random());
    toggleDropDownTabs("Hide all");
  };

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      setData([]);
      setError(null);

      try {
        switch (segmentType) {
          case "default":
            await fetchBidData();
            break;
          case "datesegments":
            await fetchBidSegments();
            break;
          default:
            await fetchBidData();
            break;
        }

        await getCustomColumns();
      } catch (error) {
        console.error("Error loading data:", error);
        setError(error.message || "An unexpected error occurred.");
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [random, dateSegments, segmentType]);

  const fetchBidData = async () => {
    try {
      let resBody = apiRequestBodyBuilder(state, { group_by: dateSegments });

      const res = await axios.post(
        "https://api.confidanto.com/bidstrategies/datewise-data",
        resBody,
      );
      setData(res.data);
      setColumns(BID_STRATEGY_COLUMNS);
    } catch (err) {
      console.error("ERROR: ", err);
      setError("Failed to fetch bid data.");
    }
  };

  const fetchBidSegments = async () => {
    try {
      let resBody = apiRequestBodyBuilder(state, { group_by: dateSegments });
      const res = await axios.post(
        "https://api.confidanto.com/bidstrategies/segment-data",
        resBody,
      );
      let formattedData = res.data.map((item) => {
        item.segments = item.segments.map((subItem) => {
          const date = new Date(subItem.date);
          let newDate = subItem.date;
          if (!isNaN(date.getTime())) {
            newDate = new Date(date).toISOString().split("T")[0];
          }

          return { ...subItem, date: newDate };
        });
        return item;
      });
      setData(res.data);

      const newCols = updateSegmentColumns(
        BID_STRATEGY_DATE_SEGMENTS_COLUMNS,
        dateSegments,
      );
      setColumns(newCols);
    } catch (err) {
      console.error("ERROR: ", err);
      setError("Failed to fetch bid segments.");
    }
  };

  const getCustomColumns = async () => {
    try {
      const requestBody = apiRequestBodyBuilder(null);

      const response = await axios.post(
        "https://api.confidanto.com/custom_columns/get-custom-columns",
        requestBody,
      );
      const temp = response.data.data.map((i) => ({
        id: i.id,
        title: i.custom_column,
        key: i.custom_column,
        visible: false,
        category: "Custom Columns",
        type: "number",
      }));
      setColumns((prevColumns) => {
        const prevColsFiltered = prevColumns.filter(
          (item) => item.category !== "Custom Columns",
        );
        const allColumns = [...prevColsFiltered, ...temp];

        const sorted = getPriorityColumnsFromStorage(allColumns);

        return sorted;
      });
    } catch (error) {
      setColumns((prevColumns) => {
        const sorted = getPriorityColumnsFromStorage(prevColumns);
        return sorted;
      });
      console.error("Error fetching custom columns:", error);
    }
  };

  const datePickerRef = useRef(null);
  const toggleFullScreen = () => setIsFullScreen(!isFullScreen);

  let SegmentRef = useRef(null);
  let ColumnRef = useRef(null);

  const toggleDropDownTabs = (type) => {
    setShowDatePicker(type === "Calendar" ? !showDatePicker : false);
    setIsGroupListVisible(type === "Segment" ? !isGroupListVisible : false);
    setShowColumnsMenu(type === "Columns" ? !showColumnsMenu : false);
    setShowDownloadMenu(type === "Download" ? !showDownloadMenu : false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      )
        setShowDatePicker(false);
      if (SegmentRef.current && !SegmentRef.current.contains(event.target))
        setIsGroupListVisible(false);
      if (ColumnRef.current && !ColumnRef.current.contains(event.target))
        setShowColumnsMenu(false);
      if (downloadRef.current && !downloadRef.current.contains(event.target))
        setShowDownloadMenu(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const [isGroupListVisible, setIsGroupListVisible] = useState(false);
  const [showViewBy, setShowViewBy] = useState(false);

  const [currentOrderType, setCurrentOrderType] = useState(true);
  const [currentOrderVariable, setCurrentOrderVariable] = useState(null);

  const changeOrderTypeCampaign = (type) => {
    const newOrderType =
      type !== currentOrderVariable ? true : !currentOrderType;
    setCurrentOrderType(newOrderType);
    setCurrentOrderVariable(type);
    const sortedData = [...data].sort((a, b) => {
      const valA = getSortableValueGLOBAL(a, type);
      const valB = getSortableValueGLOBAL(b, type);

      if (valA == null) return 1;
      if (valB == null) return -1;
      let comparison =
        typeof valA === "number" && typeof valB === "number"
          ? valA - valB
          : String(valA).localeCompare(String(valB));
      return newOrderType ? comparison : -comparison;
    });
    setData(sortedData);
  };

  const removeFilterFromArray = (id) => {
    setFilterArray((prev) => prev.filter((fil) => fil.id !== id));
  };

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  const visibleColumnsCount = columns.filter((c) => c.visible).length;

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => setCurrentPage(newPage);
  const isLockedColumn = (col) => col.isLocked || col.locked || col.sticky;

  const isFirstVisibleColumn = (columns, col) =>
    columns.filter((c) => c.visible)[0].key === col.key;

  return (
    <div ref={exportRef}>
      <div
        className={`flex h-screen bg-white ${
          isFullScreen
            ? "fixed top-0 left-0 w-full h-full z-50 overflow-x-scroll"
            : ""
        }`}
      >
        <main className="flex flex-col flex-1 w-0 overflow-hidden p-6">
          <div
            className={`transition-opacity duration-300 ${loading ? "pointer-events-none opacity-50" : ""}`}
          >
            <DateRangeSelector
              state={state}
              setState={setState}
              setRandom={setRandom}
              showDatePicker={showDatePicker}
              toggleDropDownTabs={toggleDropDownTabs}
              datePickerRef={datePickerRef}
              Page="Bid Strategies"
            />
          </div>
          <div className="flex justify-between items-center mb-4">
            <div
              className={`transition-opacity duration-300 ${loading ? "pointer-events-none opacity-50" : ""}`}
            >
              <FilterComponent
                columns={columns}
                data={data}
                filterArray={filterArray}
                setFilterArray={setFilterArray}
                segmentType={dateSegments}
              />
            </div>
            <div className="flex space-x-2 z-50">
              <div className="relative" ref={SegmentRef}>
                <button
                  disabled={loading}
                  className={`px-4 py-2 min-w-[80px] rounded flex flex-col justify-center items-center gap-1 transition-all ${
                    segmentType !== "default"
                      ? "bg-blue-100 text-blue-600"
                      : "bg-transparent text-gray-600"
                  } ${loading ? "opacity-50 cursor-not-allowed" : "hover:bg-slate-100"}`}
                  onClick={() => toggleDropDownTabs("Segment")}
                >
                  <MdOutlineSegment className="text-md" />
                  <span className="text-md">Segment</span>
                </button>
                {isGroupListVisible && (
                  <div className="absolute right-0 z-20 w-56 bg-white shadow-lg rounded-lg mt-2 p-4 border">
                    <p className="p-2 text-sm text-gray-400">By</p>
                    <button
                      className="p-2 flex items-center hover:bg-gray-50 cursor-pointer w-full justify-between"
                      onClick={() => setShowViewBy(!showViewBy)}
                    >
                      View By{" "}
                      <IoIosArrowDown
                        className={`ml-2 transform transition-transform ${
                          showViewBy ? "rotate-180" : "rotate-0"
                        }`}
                      />
                    </button>
                    {showViewBy && (
                      <ul className="mx-2">
                        <li
                          className="p-2 hover:bg-gray-100 capitalize cursor-pointer"
                          onClick={() => changeGroupbyView("default")}
                        >
                          None
                        </li>
                        <li
                          className={`p-2 hover:bg-gray-100 capitalize cursor-pointer ${
                            segmentType === "datesegments" &&
                            dateSegments === "date"
                              ? "bg-blue-100 text-blue-600"
                              : ""
                          }`}
                          onClick={() =>
                            changeGroupbyView("datesegments", "date")
                          }
                        >
                          Day
                        </li>
                        <li
                          className={`p-2 hover:bg-gray-100 capitalize cursor-pointer ${
                            segmentType === "datesegments" &&
                            dateSegments === "week"
                              ? "bg-blue-100 text-blue-600"
                              : ""
                          }`}
                          onClick={() =>
                            changeGroupbyView("datesegments", "week")
                          }
                        >
                          Week
                        </li>
                        <li
                          className={`p-2 hover:bg-gray-100 capitalize cursor-pointer ${
                            segmentType === "datesegments" &&
                            dateSegments === "month"
                              ? "bg-blue-100 text-blue-600"
                              : ""
                          }`}
                          onClick={() =>
                            changeGroupbyView("datesegments", "month")
                          }
                        >
                          Month
                        </li>
                        <li
                          className={`p-2 hover:bg-gray-100 capitalize cursor-pointer ${
                            segmentType === "datesegments" &&
                            dateSegments === "quarter"
                              ? "bg-blue-100 text-blue-600"
                              : ""
                          }`}
                          onClick={() =>
                            changeGroupbyView("datesegments", "quarter")
                          }
                        >
                          Quarter
                        </li>
                        <li
                          className={`p-2 hover:bg-gray-100 capitalize cursor-pointer ${
                            segmentType === "datesegments" &&
                            dateSegments === "year"
                              ? "bg-blue-100 text-blue-600"
                              : ""
                          }`}
                          onClick={() =>
                            changeGroupbyView("datesegments", "year")
                          }
                        >
                          Year
                        </li>
                      </ul>
                    )}
                  </div>
                )}
              </div>

              <div
                className={`relative transition-opacity duration-300 ${loading ? "pointer-events-none opacity-50" : ""}`}
                ref={downloadRef}
              >
                <button
                  disabled={loading || isDownloading}
                  className={`px-4 py-2 min-w-[80px] rounded flex flex-col justify-center items-center gap-1 transition-all ${
                    loading || isDownloading
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-slate-100"
                  } ${showDownloadMenu ? "bg-blue-100 text-blue-600" : "bg-transparent text-gray-600"}`}
                  onClick={() => toggleDropDownTabs("Download")}
                >
                  {isDownloading ? (
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  ) : (
                    <MdOutlineFileDownload className="text-md" />
                  )}
                  <span className="text-md">
                    {isDownloading ? "Downloading" : "Download"}
                  </span>
                </button>
                {showDownloadMenu && (
                  <div className="absolute right-0 z-40 w-36 bg-white shadow-lg rounded-lg mt-2 p-2 border">
                    <div
                      className="p-2 hover:bg-gray-100 cursor-pointer rounded flex items-center justify-between text-gray-700"
                      onClick={() => handleDownload("pdf")}
                    >
                      .PDF
                    </div>
                    <div
                      className="p-2 hover:bg-gray-100 cursor-pointer rounded flex items-center justify-between text-gray-700"
                      onClick={() => handleDownload("csv")}
                    >
                      .CSV
                    </div>
                  </div>
                )}
              </div>

              <button
                disabled={loading}
                className={`px-4 py-2 min-w-[80px] rounded flex flex-col justify-center items-center gap-1 transition-all ${
                  isFullScreen
                    ? "bg-blue-100 text-blue-600"
                    : "bg-transparent text-gray-600"
                } ${loading ? "opacity-50 cursor-not-allowed" : "hover:bg-slate-100"}`}
                onClick={toggleFullScreen}
              >
                {isFullScreen ? (
                  <FaCompress className="text-md" />
                ) : (
                  <FaExpand className="text-md" />
                )}
                <span className="text-md">
                  {isFullScreen ? "Collapse" : "Expand"}
                </span>
              </button>
              <div className="relative" ref={ColumnRef}>
                <button
                  disabled={loading}
                  className={`px-4 py-2 min-w-[80px] rounded flex flex-col justify-center items-center gap-1 transition-all ${
                    loading
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-slate-100"
                  } bg-transparent text-gray-600`}
                  onClick={() => toggleDropDownTabs("Columns")}
                >
                  <FaColumns className="text-md" />
                  <span className="text-md">Columns</span>
                </button>
                {showColumnsMenu && (
                  <ModifyColumns
                    columns={columns}
                    setColumns={setColumns}
                    setTableVisible={setTableVisible}
                    setShowColumnsMenu={setShowColumnsMenu}
                  />
                )}
              </div>
            </div>
          </div>
          {tableVisible && (
            <div className="">
              {filterArray.length > 0 && (
                <div className="w-full flex items-center p-2 relative">
                  <div className="flex flex-row w-11/12 overflow-auto scroll-container">
                    {filterArray.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center justify-between p-2 border rounded-lg min-w-fit mx-1"
                      >
                        <h2 className="mr-2">
                          {item.title} {item.sign}{" "}
                          {Array.isArray(item.value)
                            ? item.value.join(", ")
                            : item.value}
                        </h2>
                        <button onClick={() => removeFilterFromArray(item.id)}>
                          <IoMdClose />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="absolute right-5 text-2xl hover:bg-red-500 hover:text-white">
                    <button onClick={() => setFilterArray([])}>
                      <IoMdClose />
                    </button>
                  </div>
                </div>
              )}
              {/* Table & Skeleton Loading */}
              {loading ? (
                <TableSkeleton />
              ) : (
                <div className="overflow-auto w-full max-h-[calc(100vh-250px)] scrollbar-thin scrollbar-track-gray-200 scrollbar-thumb-gray-400">
                  {filteredData.length > 0 ? (
                    <RenderTable
                      columns={columns}
                      data={filteredData}
                      currentPage={currentPage}
                      rowsPerPage={rowsPerPage}
                      variant="performance"
                      enableExpandCollapse={state.length > 1}
                    />
                  ) : (
                    <div className="flex justify-center items-center h-40 mt-8 text-gray-600">
                      No data found
                    </div>
                  )}
                </div>
              )}
              {!loading && filteredData.length > 0 && (
                <div className="flex justify-end items-center mt-4 font-serif p-3">
                  <div className="flex items-center space-x-2 mr-4">
                    <label className="text-gray-700">Rows per page:</label>
                    <select
                      value={rowsPerPage}
                      onChange={handleRowsPerPageChange}
                      className="border rounded px-2 py-1"
                    >
                      <option>5</option>
                      <option>10</option>
                      <option>15</option>
                    </select>
                  </div>
                  <div className="flex items-center space-x-3">
                    <button
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(currentPage - 1)}
                      className="p-1 rounded disabled:opacity-50 hover:bg-gray-100"
                    >
                      <FaChevronLeft />
                    </button>
                    <span>
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      disabled={currentPage === totalPages}
                      onClick={() => handlePageChange(currentPage + 1)}
                      className="p-1 rounded disabled:opacity-50 hover:bg-gray-100"
                    >
                      <FaChevronRight />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default BidStrategies;
